import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Plus, Search } from "lucide-react";
import { listCourses, type CourseListItem, type CourseStatus } from "../api/courses";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { CreateCourseDialog } from "../components/CreateCourseDialog";
import { EmptyState } from "../components/EmptyState";
import { formatMoscowDate } from "../utils/datetime";

const STATUS_LABELS: Record<CourseStatus, string> = {
  draft: "Черновик",
  published: "Опубликован",
  archived: "Архив",
};

const STATUS_TONE: Record<CourseStatus, string> = {
  draft: "badge-warn",
  published: "badge-success",
  archived: "badge-neutral",
};

function canEditContent(roles: string[]): boolean {
  return roles.some((r) => r === "super_admin" || r === "admin" || r === "content_manager");
}

type SortKey = "title" | "status" | "lessons" | "updated";
type SortDir = "asc" | "desc";

export function CoursesPage() {
  const { user } = useCurrentUser();
  const [items, setItems] = useState<CourseListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CourseStatus | "">("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("title");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listCourses({
        status: statusFilter || undefined,
        q: debouncedQ || undefined,
        page_size: 100,
      });
      setItems(r.items);
      setTotal(r.total);
    } catch (err) {
      setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedQ]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const sorted = useMemo(() => {
    const arr = [...items];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      switch (sortKey) {
        case "title":
          return a.title.localeCompare(b.title, "ru") * dir;
        case "status":
          return a.status.localeCompare(b.status) * dir;
        case "lessons":
          return (a.lessons_count - b.lessons_count) * dir;
        case "updated":
          return (
            (new Date(a.updated_at).getTime() -
              new Date(b.updated_at).getTime()) *
            dir
          );
      }
    });
    return arr;
  }, [items, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir("asc");
    }
  }

  if (!user) return null;
  const canCreate = canEditContent(user.roles);

  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink">Курсы</h1>
          <p className="text-sm text-ink-muted">Всего: {total}</p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="btn-primary inline-flex items-center gap-1.5"
          >
            <Plus size={16} strokeWidth={2.25} />
            Новый курс
          </button>
        )}
      </header>

      {/* Filter row */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search
            size={16}
            strokeWidth={2}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
          />
          <input
            type="search"
            placeholder="Поиск по курсам…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full rounded-lg border border-line bg-white py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as CourseStatus | "")}
          className="input w-auto"
        >
          <option value="">Все статусы</option>
          <option value="draft">Черновики</option>
          <option value="published">Опубликованные</option>
          <option value="archived">Архив</option>
        </select>
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="card p-12 text-center text-ink-muted">Загрузка…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="Пока нет ни одного курса"
          description={
            canCreate
              ? "Нажми «+ Новый курс», чтобы создать первый."
              : "Контент-менеджер ещё не подготовил материалы."
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl bg-white ring-1 ring-line">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-surface-alt/40 text-left">
                <Th onClick={() => toggleSort("title")} active={sortKey === "title"} dir={sortDir}>
                  Название
                </Th>
                <Th onClick={() => toggleSort("status")} active={sortKey === "status"} dir={sortDir}>
                  Статус
                </Th>
                <Th
                  onClick={() => toggleSort("lessons")}
                  active={sortKey === "lessons"}
                  dir={sortDir}
                  align="right"
                >
                  Уроков
                </Th>
                <Th onClick={() => toggleSort("updated")} active={sortKey === "updated"} dir={sortDir}>
                  Обновлён
                </Th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-line last:border-b-0 transition hover:bg-surface-alt/30"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/courses/${c.id}`}
                      className="font-medium text-ink hover:text-brand hover:underline"
                    >
                      {c.title}
                    </Link>
                    {c.is_mandatory && (
                      <span className="badge badge-brand ml-2">Базовый</span>
                    )}
                    <div className="mt-0.5 font-mono text-[11px] text-ink-muted">{c.slug}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${STATUS_TONE[c.status]}`}>
                      {STATUS_LABELS[c.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-ink">
                    {c.lessons_count}
                  </td>
                  <td className="px-4 py-3 text-ink-muted">
                    {formatMoscowDate(c.updated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && <CreateCourseDialog onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

function Th({
  children,
  onClick,
  active,
  dir,
  align = "left",
}: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  dir: SortDir;
  align?: "left" | "right";
}) {
  return (
    <th
      className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 transition hover:text-ink ${
          active ? "text-ink" : ""
        }`}
      >
        {children}
        {active && (
          <span aria-hidden className="text-[10px]">
            {dir === "asc" ? "↑" : "↓"}
          </span>
        )}
      </button>
    </th>
  );
}
